import { ButtonProps } from "@mui/material/Button/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { useStyles } from "../styles";
import { map } from "lodash";
import { Type } from "../../../components/Offers/Type";
import { Offer, CreateOfferData } from "../../../types/offers";
import { Infos } from "../../../components/Offers/Infos";
import { Products } from "../../../components/Offers/Products";
import { Review } from "../../../components/Offers/Review";
import { useMutation, useQuery } from "react-query";
import { listOfferProducts, putOffers } from "../../../services/offers";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";
import { listInsights, listStores } from "../../../services/insights";

type EditOffersProps = {
  offer: Offer;
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  type: Yup.string().nullable().required("Tipo da oferta inválido"),
  name: Yup.string().nullable().required("Nome obrigatório"),
  startDate: Yup.date().nullable().required("Data de início inválido."),
  endDate: Yup.date().nullable().required("Data de término inválido."),
  insight: Yup.object()
    .nullable()
    .when("type", {
      is: (type) => {
        return type === "personalized";
      },
      then: (schema) => {
        return schema.required("Selecione uma audiência.");
      },
    }),
  clientOne2One: Yup.object()
    .nullable()
    .when("type", {
      is: (type) => {
        return type === "one_to_one";
      },
      then: (schema) => {
        return schema.required("Selecione uma pessoa.");
      },
    }),
  products: Yup.array()
    .of(Yup.object().nullable())
    .min(1, "Selecione ao menos um produto.")
    .required("Selecione ao menos um produto."),
});

export function EditOffers({ onClose, open, offer }: EditOffersProps) {
  const [document, setDocument] = useState<string>();
  const formikRef = useRef<FormikProps<CreateOfferData>>(null);
  const toast = useToast();

  const styles = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0);

  const steps = useMemo(() => {
    return ["Tipo", "Informações", "Produtos", "Resumo"];
  }, []);

  const offerType = useMemo(() => {
    if (offer?.id_lista_prospeccao) {
      return "personalized";
    } else if (offer?.one2one) {
      return "one_to_one";
    } else {
      return "price_two";
    }
  }, [offer]);

  useQuery(
    ["products", offer?.id_oferta],
    () => listOfferProducts(offer?.id_oferta),
    {
      enabled: Boolean(offer?.id_oferta),
      onSuccess: (response) => {
        setDocument(response[0]?.documento);
        const products = map(response, (product) => ({
          id: Number(product.idProduto),
          ean: product.SEQEAN,
          name: product.nomeProduto,
          price: Number(product.preco),
          percentage: Number(product.porcentagem),
          discount_price: Number(product.precoDesconto),
        }));
        formikRef.current?.setFieldValue("products", products);
      },
    }
  );

  useQuery(["stores", offer?.ID_LOJA], listStores, {
    enabled: Boolean(Number(offer?.ID_LOJA)),
    onSuccess: (response) => {
      const store = response?.find(
        (store) => store.id_loja === Number(offer?.ID_LOJA)
      );
      delete store.logo1;
      formikRef.current?.setFieldValue("stores", [store]);
    },
  });

  useQuery(["insights", offer?.id_lista_prospeccao], listInsights, {
    enabled:
      offerType === "personalized" && Boolean(offer?.id_lista_prospeccao),
    onSuccess: (response) => {
      const insight = response?.find(
        (insight) => insight.idListProspectei === offer?.id_lista_prospeccao
      );

      formikRef.current?.setFieldValue("insight", insight);
    },
  });

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const createOffers = useMutation(
    async (data: CreateOfferData) => {
      const response = await putOffers(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Oferta editada com sucesso!", { severity: "success" });
        queryClient.invalidateQueries(["offers"]);
        handleClose();
      },
      onError: () => {
        toast.show("Erro ao editar a oferta", { severity: "error" });
      },
    }
  );

  const handleSave = useCallback(
    async (data: CreateOfferData) => {
      await createOffers.mutateAsync(data);
    },
    [createOffers]
  );

  const handleValidateStep = useCallback((): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        switch (activeStep) {
          case 0:
            await formikRef.current?.validateField("type");
            break;
          case 1:
            await formikRef.current?.validateField("name");
            await formikRef.current?.validateField("clientOne2One");
            await formikRef.current?.validateField("insight");
            await formikRef.current?.validateField("startDate");
            await formikRef.current?.validateField("endDate");
            break;
          case 2:
            await formikRef.current?.validateField("products");
            break;
        }

        resolve(formikRef.current?.isValid);
      } catch (err) {
        reject(false);
      }
    });
  }, [activeStep, formikRef]);

  const handleGoBack = useCallback(() => {
    formikRef.current?.setErrors({});
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleGoNext = useCallback(async () => {
    try {
      const isValid = await handleValidateStep();
      if (isValid) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    }
  }, [handleValidateStep]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => handleGoBack(),
        disabled: createOffers.isLoading || activeStep === 0,
      },
      {
        disabled: createOffers.isLoading,
        children: createOffers.isLoading
          ? "Enviado solicitação..."
          : activeStep === 3
          ? "Editar"
          : "Continuar",

        variant: "contained",
        onClick: () =>
          activeStep === 3 ? formikRef.current?.handleSubmit() : handleGoNext(),
      },
    ];
  }, [activeStep, createOffers, formikRef, handleGoNext, handleGoBack]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <Type />;
      case 1:
        return (
          <Infos clientDocument={document} storeId={Number(offer.ID_LOJA)} />
        );
      case 2:
        return <Products />;
      case 3:
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep, offer, document]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      title={offer?.nome_oferta}
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          initialValues={
            {
              type: offerType,
              name: offer?.nome_oferta,
              startDate: new Date(offer?.data_inicio.replace("Z", "")),
              endDate: new Date(offer?.data_fim.replace("Z", "")),
              products: [],
              stores: [],
              insight: undefined,
              offerId: offer?.id_oferta,
            } as CreateOfferData
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSave}
        >
          {renderContent()}
        </Formik>
      </div>
    </Dialog>
  );
}
